import { AemMarketConfig, FalconFields, FalconNextBestActionType, NextBestActionType } from '~/app/market-config';
import {
	CMS_DEFAULT_STOCKCAR_MODELPRECISION_CODE_PRECISION,
	CMS_DEFAULT_STOCKCAR_MODELPRECISION_END,
	CMS_DEFAULT_STOCKCAR_MODELPRECISION_START,
	CMS_DEFAULT_STOCKCAR_PRICE_MAX,
	CMS_DEFAULT_STOCKCAR_PRICE_MIN,
	CMS_DEFAULT_STOCKCAR_RANGE_MAX,
	CMS_DEFAULT_STOCKCAR_RANGE_MIN,
	CMS_DEFAULT_STOCKCAR_RANGE_STEP,
} from '~/app/models/constans';

export function mapFalconToAemMarketConfig(fields: FalconFields): AemMarketConfig {
	return {
		market: fields.market,
		language: fields.language,
		carlineId: fields.carlineId,
		hideStandardAccessories: fields.hideStandardAccessories,
		stockCarConfig: fields.stockCarConfig
			? {
					enabled: true,
					url: fields.stockCarConfig.fields.url || '',
					priceFilter: {
						min: fields.stockCarConfig.fields.priceFilter_min || CMS_DEFAULT_STOCKCAR_PRICE_MIN,
						max: fields.stockCarConfig.fields.priceFilter_max || CMS_DEFAULT_STOCKCAR_PRICE_MAX,
					},
					rangeFilter: {
						min: fields.stockCarConfig.fields.rangeFilter_min || CMS_DEFAULT_STOCKCAR_RANGE_MIN,
						max: fields.stockCarConfig.fields.rangeFilter_max || CMS_DEFAULT_STOCKCAR_RANGE_MAX,
						step: fields.stockCarConfig.fields.rangeFilter_step || CMS_DEFAULT_STOCKCAR_RANGE_STEP,
					},
					geoFilter: fields.stockCarConfig.fields.geoFilter,
					subheader: fields.stockCarConfig.fields.subheader,
					disclaimer: fields.stockCarConfig.fields.disclaimer,
					pricedisclaimer: fields.stockCarConfig.fields.pricedisclaimer,
					modelPrecision: {
						codePrecision:
							fields.stockCarConfig.fields.modelPrecision_codePrecision ||
							CMS_DEFAULT_STOCKCAR_MODELPRECISION_CODE_PRECISION,
						start:
							fields.stockCarConfig.fields.modelPrecision_start ||
							CMS_DEFAULT_STOCKCAR_MODELPRECISION_START,
						end: fields.stockCarConfig.fields.modelPrecision_end || CMS_DEFAULT_STOCKCAR_MODELPRECISION_END,
					},
				}
			: undefined,
		nbas: fields.nbas && fields.nbas.length > 0 ? mapNbas(fields.nbas) : undefined,
		additionalPrimaryNba: fields.additionalPrimaryNba
			? {
					url: fields.additionalPrimaryNba.fields.url,
					target: fields.additionalPrimaryNba.fields.target,
					description: fields.additionalPrimaryNba.fields.description,
					labelText: fields.additionalPrimaryNba.fields.labelText,
					icon: fields.additionalPrimaryNba.fields.icon,
					trackingName: fields.additionalPrimaryNba.fields.trackingName,
					botID: fields.additionalPrimaryNba.fields.botID,
				}
			: undefined,
		combinations:
			fields.configuratorSteps && fields.configuratorSteps.length > 0
				? fields.configuratorSteps.map((a) => a.fields.stepName)
				: undefined,
		summaryFooterCta:
			fields.summaryFooterCta && Array.isArray(fields.summaryFooterCta) && fields.summaryFooterCta?.length > 0
				? {
						url: fields.summaryFooterCta[0].fields.url,
						target: fields.summaryFooterCta[0].fields.target,
						description: fields.summaryFooterCta[0].fields.description,
						labelText: fields.summaryFooterCta[0].fields.labelText,
						icon: '',
						trackingName: fields.summaryFooterCta[0].fields.trackingName,
						botID: fields.summaryFooterCta[0].fields.botID,
					}
				: fields.summaryFooterCta && !Array.isArray(fields.summaryFooterCta)
					? {
							url: fields.summaryFooterCta.fields.url,
							target: fields.summaryFooterCta.fields.target,
							description: fields.summaryFooterCta.fields.description,
							labelText: fields.summaryFooterCta.fields.labelText,
							icon: '',
							trackingName: fields.summaryFooterCta.fields.trackingName,
							botID: fields.summaryFooterCta.fields.botID,
						}
					: undefined,
		secondarySummaryCta:
			fields.summaryFooterCta && Array.isArray(fields.summaryFooterCta) && fields.summaryFooterCta.length > 1
				? {
						url: fields.summaryFooterCta[1].fields.url,
						target: fields.summaryFooterCta[1].fields.target,
						description: fields.summaryFooterCta[1].fields.description,
						labelText: fields.summaryFooterCta[1].fields.labelText,
						icon: '',
						trackingName: fields.summaryFooterCta[1].fields.trackingName,
						botID: fields.summaryFooterCta[1].fields.botID,
					}
				: undefined,
		disablePdfNba: fields.disablePdfNba,
		omnibus: fields.omnibusUrl ? { enabled: true, url: fields.omnibusUrl } : undefined,
		deliveryCostUrl: fields.deliveryCostUrl,
		showAccessoriesInclusive: fields.showAccessoriesInclusive,
		showAccessoriesExclusive: fields.showAccessoriesExclusive,
		enableIncentives: fields.enableIncentives,
		specialOfferUrl: fields.specialOfferUrl,
		enableFinancingServices: fields.enableFinancingServices,
		hideMonthlyRates: fields.hideMonthlyRates,
		showCaESummaryText: fields.showCaESummaryText,
		showCaETextEngineCard: fields.showCaETextEngineCard,
		engineCardKeyfacts: fields.engineCardKeyfacts?.length > 0 ? fields.engineCardKeyfacts : undefined,
		extendedTechnicalAttributes:
			fields.extendedTechnicalAttributes_wltp && fields.extendedTechnicalAttributes_wltp.length > 0
				? { wltp: fields.extendedTechnicalAttributes_wltp }
				: undefined,
		enableDynamicSummaryFooterButton: fields.enableDynamicSummaryFooterButton,
		enableEngineFilter: fields.enableEngineFilter,
		enable3d: fields.enable3d,
		globalDisclaimer: fields.globalDisclaimer
			? { disclaimer: fields.globalDisclaimer.fields.disclaimer, variant: fields.globalDisclaimer.fields.variant }
			: undefined,
		categoryDisclaimers:
			fields.configuratorSteps && fields.configuratorSteps.length > 0
				? fields.configuratorSteps.map((d) => ({ step: d.fields.stepName, disclaimer: d.fields.disclaimer }))
				: undefined,
		leanCheckoutEnabled: fields.leanCheckoutEnabled,
		footnoteEngine: fields.footnoteEngine,
	} as AemMarketConfig;
}

function mapNbas(nbas: FalconNextBestActionType[]): NextBestActionType[] {
	const nbasConverted: NextBestActionType[] = [];
	nbas.forEach((nb) => {
		nbasConverted.push({
			icon: nb.fields.icon,
			trackingName: nb.fields.trackingName,
			labelText: nb.fields.labelText,
			description: nb.fields.description,
			url: nb.fields.url,
			target: nb.fields.target,
			botID: nb.fields.botID,
		});
	});
	return nbasConverted;
}
